
import { of as observableOf,  Subject ,  Observable } from 'rxjs'

import { mergeMap,  catchError ,  tap } from 'rxjs/operators'
import * as React from 'react'
import AdminPage from 'admin/components/AdminPage'
import { HomeContentBlock as IHomeContentBlock } from 'admin/interfaces'
import { useDispatch } from 'react-redux'
import {
  createHomeBlock,
  deleteHomeBlock,
  getHomeContentBlocks,
  reorderHomeBlocks,
  updateHomeBlock
} from 'admin/services/home-config/actions'
import { StoreThunkDispatch } from 'store/state'
import { message } from 'services/snackbar'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import { HomeContentBlock, HomeContentBlockTableHeader } from './components/HomeContentBlock'
import { DndContext, MeasuringStrategy } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import Input from '@mui/material/TextField'
import Button from '@mui/material/Button'
import styles from './HomeConfig.pcss'
import { reorder } from 'shared/utils'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

export function HomeConfig() {
  const [blocks, setBlocks] = React.useState<IHomeContentBlock[]>([])
  const [loading, setLoading] = React.useState(true)
  const [newBlock, setNewBlock] = React.useState({ name: '', key: '' })
  const dispatch = useDispatch<StoreThunkDispatch>()

  const fetch$ = React.useRef<Subject<void>>(undefined)
  const reorder$ = React.useRef<Subject<string[]>>(undefined)
  const update$ = React.useRef<Subject<IHomeContentBlock>>(undefined)
  const delete$ = React.useRef<Subject<string>>(undefined)
  const create$ = React.useRef<Subject<{ name: string, key: string, order: number }>>(undefined)

  React.useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current.pipe(
      mergeMap(() => {
        setLoading(true)
        return dispatch(getHomeContentBlocks()).pipe(catchError(() => {
          dispatch(message('Could not fetch data! Please refresh.'))
          return observableOf([])
        }))
      }))
      .subscribe(response => {
        if (response.length > 0) {
          setBlocks(response)
        }
        setLoading(false)
      })

    fetch$.current.next()
  }, [dispatch])

  React.useEffect(() => {
    reorder$.current = new Subject()
    update$.current = new Subject()
    delete$.current = new Subject()
    create$.current = new Subject()

    create$.current.pipe(
      mergeMap(({ name, key, order }) => dispatch(createHomeBlock(name, key, order)).pipe(
        tap(() => {
          dispatch(message('Block created!'))
        }),
        catchError(() => {
          dispatch(message('Action failed!'))
          return observableOf({})
        })
      )))
      .subscribe(() => {
        fetch$.current?.next()
      })

    reorder$.current.pipe(
      mergeMap((ids: string[]) => {
        return dispatch(reorderHomeBlocks(ids)).pipe(
          tap(() => {
            dispatch(message('Reorder successful!'))
          }),
          catchError(() => {
            dispatch(message('Reorder failed!'))
            return observableOf({})
          })
        )
      }))
      .subscribe(() => {
        fetch$.current?.next()
      })

    update$.current.pipe(
      mergeMap((data) => {
        return dispatch(updateHomeBlock(data)).pipe(
          tap(() => {
            dispatch(message('Update successful!'))
          }),
          catchError(() => {
            dispatch(message('Update failed!'))
            return observableOf({})
          })
        )
      }))
      .subscribe(() => {
        fetch$.current?.next()
      })

    delete$.current.pipe(
      mergeMap((id) => {
        return dispatch(deleteHomeBlock(id)).pipe(
          tap(() => {
            dispatch(message('Delete successful!'))
          }),
          catchError(() => {
            dispatch(message('Delete failed!'))
            return observableOf({})
          })
        )
      }))
      .subscribe(() => {
        fetch$.current?.next()
      })
  }, [dispatch])

  const onDragEnd = (data: any) => {
    const { active, over } = data
    const oldIndex = blocks.findIndex(b => b.id === active.id)
    const newIndex = blocks.findIndex(b => b.id === over.id)
    const reordered = reorder(blocks, oldIndex, newIndex)
    setBlocks(reordered)
    setLoading(true)
    reorder$.current?.next(reordered.map(b => b.id))
  }

  const onBlockUpdate = (block: IHomeContentBlock) => {
    setLoading(true)
    update$.current?.next(block)
  }

  const onDeleteBlock = (id: string) => {
    setLoading(true)
    delete$.current?.next(id)
  }

  const createBlock = () => {
    const name = newBlock.name.trim()
    const key = newBlock.key.trim()
    if (name.length > 0 && key.length > 0) {
      setLoading(true)
      const order = blocks.length
      create$.current?.next({ ...newBlock, order })
      setNewBlock({ name: '', key: '' })
    } else {
      alert('Please enter name and key')
    }
  }

  const onNewBlockChange = (prop: 'name' | 'key') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewBlock(current => ({ ...current, [prop]: value }))
  }

  return (
    <AdminPage title="Home Carousels">
      <Backdrop open={loading} className={styles.backdrop}>
        <CircularProgress color="primary" />
      </Backdrop>
      <HomeContentBlockTableHeader />
      <DndContext
        modifiers={[restrictToVerticalAxis]}
        layoutMeasuring={{ strategy: MeasuringStrategy.Always }}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={blocks.map(b => b.id)} strategy={rectSortingStrategy}>
          {blocks.map((block) => (
            <HomeContentBlock
              key={block.id}
              block={block}
              onUpdate={onBlockUpdate}
              onDelete={onDeleteBlock}
            />
          ))}
        </SortableContext>
      </DndContext>
      <div className={styles.footer}>
        <h3>Create new carousel</h3>
        <Input value={newBlock.name} placeholder="Carousel Name" onChange={onNewBlockChange('name')} />
        <Input value={newBlock.key} placeholder="NEW_CAROUSEL_KEY" onChange={onNewBlockChange('key')} />
        <Button variant="contained" color="primary" size="small" onClick={createBlock}>Create</Button>
      </div>
    </AdminPage>
  )
}

export default HomeConfig
